// Utilidades comunes de los renderers de 80 mm. Todo texto que venga del DTO
// pasa por escaparHtml: nombres de producto y observaciones se imprimen como
// texto, nunca como marcado.

// Calibrado con la comandera del evento: Gadnic POS-80C (familia Xprinter
// XP-80C), 203 dpi = 8 puntos por mm, 576 puntos de ancho. Su driver genérico
// define el papel como "80(72.1) × 297 mm": la página que ve Chrome mide
// 72,1 mm, no 80. @page tiene que medir exactamente eso; con 80 mm Chrome
// escalaba todo al 90 % y el QR salía ilegible en papel aunque se leyera en
// pantalla. Si cambia la impresora, estos son los números a recalibrar.
export const PUNTOS_POR_MM = 8;
export const ANCHO_PAGINA_MM = 72.1;
export const MARGEN_LATERAL_MM = 1;
export const ANCHO_UTIL_MM = 70;

// QR fiscal: cada módulo debe ocupar un número entero de puntos del cabezal
// para que no haya módulos de distinto ancho ni reescalado. Probado en papel
// con la POS-80C: 6 puntos (0,75 mm, ~52 mm de lado) se lee siempre; con 5 o
// menos ya no. Si el comprobante cae en una versión más grande y no entra en
// el ancho útil, se baja de a un punto, sabiendo que eso compromete la lectura.
export const PUNTOS_POR_MODULO_QR = 6;
export const MARGEN_QR_MODULOS = 4;

export const calcularLadoQrMm = (modulosConMargen) => {
  const modulos = Number(modulosConMargen) || 0;
  if (modulos <= 0) return ANCHO_UTIL_MM;
  for (let puntos = PUNTOS_POR_MODULO_QR; puntos >= 3; puntos -= 1) {
    const lado = (modulos * puntos) / PUNTOS_POR_MM;
    if (lado <= ANCHO_UTIL_MM) return lado;
  }
  return ANCHO_UTIL_MM;
};

const REEMPLAZOS_HTML = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

export const escaparHtml = (valor) =>
  String(valor ?? "").replace(/[&<>"']/g, (caracter) => REEMPLAZOS_HTML[caracter]);

// Espacio común en lugar del duro que pone Intl: el resultado es el mismo en
// cualquier motor y no depende de cómo lo trate la fuente de la impresora.
export const formatearPesos = (valor) =>
  new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
    .format(Number(valor || 0))
    .replace(/ /g, " ");

const aFecha = (valor) => {
  if (!valor) return null;
  if (valor instanceof Date) return Number.isNaN(valor.getTime()) ? null : valor;
  // Las fechas DATEONLY llegan como "YYYY-MM-DD"; se interpretan como locales
  // para que no retrocedan un día por la zona horaria.
  const texto = String(valor);
  const fecha = /^\d{4}-\d{2}-\d{2}$/.test(texto)
    ? new Date(`${texto}T00:00:00`)
    : new Date(texto.replace(" ", "T"));
  return Number.isNaN(fecha.getTime()) ? null : fecha;
};

export const formatearFecha = (valor) => {
  const fecha = aFecha(valor);
  return fecha ? fecha.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "";
};

// La operación es un instante real y el papel se lee en Villa Mercedes: se
// imprime en la zona del evento (la misma que fija su vigencia), no en la del
// sistema donde corre el navegador o los tests.
const ZONA_HORARIA_EVENTO = "America/Argentina/Buenos_Aires";

export const formatearFechaHora = (valor) => {
  const fecha = aFecha(valor);
  if (!fecha) return "";
  const dia = fecha.toLocaleDateString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: ZONA_HORARIA_EVENTO,
  });
  const hora = fecha.toLocaleTimeString("es-AR", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: ZONA_HORARIA_EVENTO,
  });
  return `${dia} ${hora}`;
};

// AFIP devuelve el vencimiento del CAE como "AAAAMMDD".
export const formatearFechaAfip = (valor) => {
  const texto = String(valor || "").trim();
  const partes = texto.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (partes) return `${partes[3]}/${partes[2]}/${partes[1]}`;
  return formatearFecha(texto) || texto;
};

// Hoja única, monocroma, sin depender de fondos de color ni de fuentes
// externas: lo que imprime una térmica de 80 mm es tinta negra sobre papel.
// Compacta a propósito: el papel es el costo. El margen inferior es el mínimo
// para que el corte del driver no pise la última línea.
export const hojaImpresion80mm = `
  @page { size: ${ANCHO_PAGINA_MM}mm auto; margin: 0; }
  html, body { margin: 0; padding: 0; background: #fff; color: #000; }
  body {
    box-sizing: border-box;
    width: ${ANCHO_PAGINA_MM}mm;
    padding: 1.5mm ${MARGEN_LATERAL_MM}mm 5mm;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 9pt;
    line-height: 1.15;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  * { box-sizing: border-box; }
  h1, h2, p { margin: 0; }
  h1 { font-size: 12pt; font-weight: 700; text-align: center; }
  h2 { font-size: 10pt; font-weight: 700; }
  .centro { text-align: center; }
  .chico { font-size: 7.5pt; }
  .negrita { font-weight: 700; }
  .sep { border-top: 1px dashed #000; margin: 1.2mm 0; }
  .fila { display: flex; justify-content: space-between; gap: 2mm; }
  .fila > :last-child { text-align: right; white-space: nowrap; }
  .aviso { border: 1.5px solid #000; padding: 1mm; text-align: center; font-weight: 700; margin: 1.2mm 0; font-size: 9.5pt; }
  table { width: 100%; border-collapse: collapse; }
  td { padding: 0.3mm 0; vertical-align: top; text-align: left; }
  .num { text-align: right; white-space: nowrap; }
  .cant { width: 6mm; white-space: nowrap; }
  .unitario { font-size: 7.5pt; }
  .obs { font-size: 7.5pt; padding-left: 6mm; font-style: italic; }
  .qr { display: block; margin: 1.2mm auto 0; }
  .qr svg { display: block; width: 100%; height: 100%; shape-rendering: crispEdges; }
  .numero-pedido { display: flex; align-items: baseline; justify-content: center; gap: 2mm; }
  .numero-pedido .numero { font-size: 20pt; font-weight: 700; line-height: 1; }
  .comanda-linea { font-size: 11.5pt; font-weight: 700; padding: 0.6mm 0; border-bottom: 1px dotted #000; }
  .comanda-linea .cant { font-size: 12.5pt; width: 10mm; }
  .comanda-obs { margin-top: 1.2mm; padding: 1mm; border: 1.5px solid #000; }
  .comanda-obs .titulo { font-size: 8pt; font-weight: 700; letter-spacing: .06em; }
  .comanda-obs .texto { font-size: 10pt; font-weight: 700; white-space: pre-line; overflow-wrap: anywhere; }
`;

// Número de pedido grande, igual en los tres papeles: es lo que el comprador
// compara con la comanda cuando lo llaman a retirar.
export const numeroPedidoDe = (pedido) => String(pedido?.nroPedido || pedido?.idPedido || "");

export const cabeceraNumeroPedido = (pedido, etiqueta = "PEDIDO") =>
  `<div class="numero-pedido"><span class="negrita">${escaparHtml(etiqueta)}</span><span class="numero">${escaparHtml(
    numeroPedidoDe(pedido)
  )}</span></div>`;

export const envolverDocumento = ({ titulo, cuerpo }) => `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8">
<title>${escaparHtml(titulo)}</title>
<style>${hojaImpresion80mm}</style>
</head>
<body>${cuerpo}</body>
</html>`;
